import React from 'react';
import { useSelector } from 'react-redux';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { groupsMemberRequestApproveDecline } from '@wix/bi-logger-groups/v2';

import type { IMember } from '@wix/social-groups-serverless/dist/members/types';
import { selectJoinRequestStatus } from 'store/selectors';

import { InputDialog } from 'wui/InputDialog';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogContent } from 'wui/DialogContent';
import { DialogActions } from 'wui/DialogActions';
import { TextArea } from 'wui/TextArea';
import { Button } from 'wui/Button';

import { useDidUpdate } from 'common/hooks';
import { useController } from 'common/context/controller';

interface IRejectDialogProps {
  isOpen: boolean;
  groupId: string;
  profile: IMember;

  onClose(): void;
}

export function RejectDialog(props: IRejectDialogProps) {
  const { isOpen, onClose, groupId, profile } = props;

  const bi = useBi();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const [reason, setReason] = React.useState('');
  const { members$ } = useController();
  const status = useSelector(
    selectJoinRequestStatus(profile.siteMemberId as string),
  );

  useDidUpdate(() => {
    if (!status.reject.loading && !status.reject.error) {
      handleClose();
    }
  }, [status.reject.loading, status.reject.error]);

  return (
    <InputDialog
      isOpen={isOpen}
      onClose={handleClose}
      aria-labelledby="rm-dialog-title"
    >
      <DialogTitle
        id="rm-dialog-title"
        title={t('groups-web.members.reject-dialog.title')}
        subtitle={t('groups-web.members.reject-dialog.caption', {
          name: profile.name,
        })}
      />
      <DialogContent divider>
        <TextArea
          showCharCount
          maxLength={100}
          value={reason}
          error={status.reject.error}
          onChange={handleReasonChange}
          label={t('groups-web.members.reject-dialog.reason.label')}
          ariaLabel={t('groups-web.members.reject-dialog.reason.placeholder')}
          placeholder={t('groups-web.members.reject-dialog.reason.placeholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button
          outlined
          variant="basic"
          fullWidth={isMobile}
          onClick={handleClose}
        >
          {t('groups-web.cancel')}
        </Button>
        <Button
          variant="basic"
          fullWidth={isMobile}
          onClick={handleSubmit}
          disabled={status.reject.loading}
          loading={status.reject.loading}
        >
          {t('groups-web.members.pending.decline')}
        </Button>
      </DialogActions>
    </InputDialog>
  );

  function handleReasonChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setReason(event.target.value);
  }

  function handleSubmit() {
    members$.reject(groupId, profile.siteMemberId as string, reason);
    bi.report(
      groupsMemberRequestApproveDecline({
        groupId,
        isAll: false,
        memberId: profile.siteMemberId,
        origin: 'user_lvl_btn',
      }),
    );
  }

  function handleClose() {
    onClose();
  }
}

RejectDialog.displayName = 'RejectDialog';
